<template>
  <div>
    <debug>
      {{ value }}
    </debug>
    <!-- TEMPLATE DETAIL -->
    <m-header
      :title="`${getFlagEmojiByLanguage(value.meta.language)} ${value.meta.title}`"
      :alerts="alerts"
      :subtitle="value.meta.description"
      :breadCrumbs="breadCrumbs"
      :chips="chips"
      :actions="actions"
      class="mb-2"
    />

    <v-text-field
      v-model="value.content.subject"
      outlined
      auto-grow
      :readonly="true"
      :label="$t('components.template.editor.subject')"
    />
    <v-textarea
      v-if="value.meta.contentFormat === MessageContentFormatEnum.PLAIN"
      v-model="value.content.body"
      outlined
      auto-grow
      readonly
      :label="$t('components.template.detail.body.body')"
    />
    <template-editor
      v-else
      v-model="value.content.body"
      :isPlain="false"
      :readOnly="true"
      :partnerId="value.partnerId"
      :label="$t('components.template.detail.body.body')"
    />

    <template-detail-language-dialog
      v-if="value"
      ref="languageOptions"
      :template="value"
      :partnerId="value.partnerId"
    />
    <confirm-action-dialog
      :isDialogActive.sync="isDeleteDialogActive"
      :title="$t('components.template.detail.delete.title')"
      :leftText="$t('components.template.detail.delete.abort')"
      :rightText="$t('components.template.detail.delete.delete')"
      rightColor="error"
      :loading="isLoadingDelete"
      :rightLoading="isLoadingDelete"
      @close="isDeleteDialogActive = false"
      @leftClick="isDeleteDialogActive = false"
      @rightClick="deleteTemplate"
    />
    <confirm-action-dialog
      v-if="isAdmin"
      :isDialogActive.sync="isMakeAdminTemplateDialogActive"
      :title="$t('components.template.detail.makeAdminTemplate.title')"
      :subtitle="$t('components.template.detail.makeAdminTemplate.body')"
      :leftText="$t('components.template.detail.makeAdminTemplate.abort')"
      :rightText="$t('components.template.detail.makeAdminTemplate.confirm')"
      :loading="isLoadingMakeAdminTemplate"
      :rightLoading="isLoadingMakeAdminTemplate"
      @close="isMakeAdminTemplateDialogActive = false"
      @leftClick="isMakeAdminTemplateDialogActive = false"
      @rightClick="makeAdminTemplate"
    />
  </div>
</template>

<script lang="ts">
import TemplateEditor from "@/components/template/TemplateEditor.vue";
import ConfirmActionDialog from "@/components/utility/ConfirmActionDialog.vue";
import { getFlagEmojiByLanguage } from "@/lib/CountryCodeHelper";
import { MessageContentFormatEnum } from "@/lib/enum/templateEnums/messageContentFormat.enum";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import { $t } from "@/lib/utility/t";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import RulesMixin from "@/mixins/RulesMixin.vue";
import { AdminTemplate } from "@/models/admin-template.entity";
import { IPartnerTemplate } from "@/models/partner-template.entity";
import { ITemplate } from "@/models/template.entity";
import { MrfiktivPartnerTemplateViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import LatestEntriesCardEmpty from "../cards/LatestEntriesCardEmpty.vue";
import Card from "../utility/Card.vue";
import MHeader, { IAction, IAlert } from "../utility/mmmint/MHeader.vue";
import TemplateCreateDialog from "./TemplateCreateDialog.vue";
import TemplateDetailLanguageDialog from "./TemplateDetailLanguageDialog.vue";
import TemplateEditorSubject from "./TemplateEditorSubject.vue";
import { TemplateGoToHelper } from "@/lib/utility/template.go-to-helper";
import Debug from "../utility/Debug.vue";

@Component({
  components: {
    MHeader,
    LatestEntriesCardEmpty,
    Card,
    ConfirmActionDialog,
    TemplateEditorSubject,
    TemplateCreateDialog,
    TemplateDetailLanguageDialog,
    TemplateEditor,
    Debug
  },
  filters: { getFlagEmojiByLanguage }
})
export default class TemplateDetail extends mixins(DarkModeHighlightMixin, PermissionMixin, RulesMixin) {
  readonly getFlagEmojiByLanguage = getFlagEmojiByLanguage;
  readonly MessageContentFormatEnum = MessageContentFormatEnum;

  @Prop()
  value!: ITemplate;

  @Prop({ default: true })
  displayToDetailView!: boolean;

  @Prop({ default: false })
  hideBreadCrumbs!: boolean;

  isDeleteDialogActive = false;

  isLoadingDelete = false;

  isMakeAdminTemplateDialogActive = false;

  isLoadingMakeAdminTemplate = false;

  get alerts(): IAlert[] {
    const alerts: IAlert[] = [];

    if ((this.value as MrfiktivPartnerTemplateViewModelGen)?.isAdminTemplate) {
      alerts.push({
        text: $t("components.template.detail.delete.systemMail"),
        type: "info"
      });
    }

    return alerts;
  }

  get chips() {
    const chips = [];

    chips.push({
      text: this.value ? simpleDoubleDigitDate(this.value.timestamp.created) : "",
      color: "info"
    });

    if (!(this.value as IPartnerTemplate).partnerId) {
      if ((this.value as IPartnerTemplate).isPublic) {
        chips.push({
          text: $t("template.public"),
          color: "primary"
        });
      } else {
        chips.push({
          text: $t("template.private"),
          color: "primary"
        });
      }
    }

    for (const cat of this.value?.meta.categories ?? []) {
      chips.push({
        text: cat,
        color: "primary"
      });
    }

    if (this.value?.meta?.contentFormat === MessageContentFormatEnum.HTML) {
      chips.push({
        text: $t("enums.MessageContentFormatEnum.html"),
        color: "primary"
      });
    } else if (this.value?.meta?.contentFormat === MessageContentFormatEnum.PLAIN) {
      chips.push({
        text: $t("enums.MessageContentFormatEnum.plain"),
        color: "primary"
      });
    }

    return chips;
  }

  get breadCrumbs() {
    if (this.hideBreadCrumbs) {
      return undefined;
    }

    const breadCrumbs = TemplateGoToHelper.breadCrumbs;

    const selected = [];

    const partnerId = (this.value as IPartnerTemplate).partnerId;
    const templateId = this.value.id;

    selected.push(breadCrumbs.TemplateTable({ partnerId }));
    selected.push(breadCrumbs.TemplateDetail({ partnerId, templateId }));

    return selected;
  }

  get actions(): IAction[] {
    const actions: IAction[] = [];

    if (this.displayToDetailView) {
      actions.push({
        key: "toDetail",
        text: $t("components.template.detail.toDetail"),
        exec: this.value.goTo(this.$router).detail
      });
    }

    actions.push({
      key: "toDetail",
      text: $t("common.verbs.edit"),
      exec: this.value.goTo(this.$router).form
    });

    if (this.can(this.ActionEnum.UPDATE, this.ResourceEnum.TEMPLATE)) {
      actions.push({
        key: "createForNewLanguage",
        text: $t("components.template.detail.language.title"),
        exec: () => (this.$refs.languageOptions as TemplateDetailLanguageDialog)?.open()
      });
    }

    if (this.isAdmin && (this.value as IPartnerTemplate).partnerId) {
      actions.push({
        key: "makeAdminTemplate",
        text: $t("components.template.detail.makeAdminTemplate.action"),
        exec: () => {
          this.isMakeAdminTemplateDialogActive = true;
        }
      });
    }

    if (
      this.value &&
      !(this.value as IPartnerTemplate).isAdminTemplate &&
      this.can(this.ActionEnum.DELETE, this.ResourceEnum.TEMPLATE)
    ) {
      actions.push({
        key: "bodyUpdate",
        text: $t("delete"),
        exec: () => {
          this.isDeleteDialogActive = true;
        },
        color: "error"
      });
    }

    return actions;
  }

  async deleteTemplate() {
    this.isLoadingDelete = true;
    try {
      await this.value.delete();
      this.value.goTo(this.$router).table();
    } catch (e) {
      handleError(e);
    } finally {
      this.isDeleteDialogActive = false;
      this.isLoadingDelete = false;
      this.$emit("onDelete");
    }
  }

  async makeAdminTemplate() {
    this.isLoadingMakeAdminTemplate = true;
    try {
      const createdTemplate = await AdminTemplate.copyFromPartnerTemplate(this.value).create();

      createdTemplate?.goTo(this.$router).detail();
    } catch (e) {
      handleError(e);
    } finally {
      this.isMakeAdminTemplateDialogActive = false;
      this.isLoadingMakeAdminTemplate = false;
    }
  }
}
</script>

<style scoped>
.wrapper {
  height: calc(100%);
}
.subtitle {
  margin-left: 1px;
  margin-right: 1px;
}
.virtual-scroller {
  height: calc(100vh - 180px);
  overflow-y: hidden;
}
</style>
